import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import NavBar from '../NavBar';
import { Page, DataGrid, Utils, Icon } from 'ecrion-components';
import DocumentTitle from 'react-document-title';
import PageNotFound from '../PageNotFound';
import { useUser } from '../../context/UserContext';
import { getMicroservicesInfo } from '../../utils/microserviceInfoClient';
import Integration from '../Integration';

const Home = ({ match, history, location }) => {
  const [customPins, setCustomPins] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [error, setError] = useState('');

  const userEnvironment = useUser().Environment;
  const encodedUserEnvironment = encodeURIComponent(userEnvironment);

  let receivedEnvironment = decodeURIComponent(match.params.environment || '');
  if (receivedEnvironment && receivedEnvironment.toLowerCase() !== userEnvironment.toLowerCase()) {
    return <PageNotFound history={history} />;
  }

  let activeTab = decodeURIComponent(match.params.pinId || '');

  React.useEffect(() => {
    let redirect = false;
    /** In the case of an environment containing '&' ~ 'RCS & RDS' ~
     * which in the URL should look like "RCS%20%26%20RDS",
     * location.pathname === "RCS %26 RDS" thus it is necessary to
     * replace '&' with '%26 in userEnvironment'
     */
    if (
      !location.pathname.startsWith(`/${userEnvironment.replace(/&/g, '%26')}`)
    )
      redirect = true;

    if (!activeTab || activeTab === 'Login') {
      activeTab = 'forms';
      redirect = true;
    }

    if (redirect) {
      history.replace(`/${encodedUserEnvironment}/${activeTab}`);
    }
  }, [location]);

  React.useEffect(() => {
    let isMounted = true;
    getMicroservicesInfo(userEnvironment)
      .then((microservices) => {
        const pins = microservices.map((microservice) => {
          return {
            label: microservice.name,
            icon: microservice.iconURL,
            value: microservice.name,
            data: microservice,
          };
        });
        if (isMounted) {
          setCustomPins(pins);
          if (error) setError('');
        }
      })
      .catch((err) => {
        if (isMounted) {
          setOpenSnackbar(true);
          setError(Utils.getPrettyError(err));
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const customActiveTab = customPins.find((pin) => pin.value === activeTab);
  let documentTitle = customActiveTab ? customActiveTab.label : activeTab;
  if (documentTitle)
    documentTitle = documentTitle[0].toUpperCase() + documentTitle.substring(1);

  const changeActiveTab = (value) => {
    history.push(`/${encodedUserEnvironment}/${value}`);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <DocumentTitle title={`${documentTitle || 'Home'} - Customer Portal`}>
      <>
        <Page.NavBar variant="custom-children">
          <NavBar />
        </Page.NavBar>
        <div className={`common-page`}>
          <Page.Common
            sidebar={{
              options: [
                {
                  label: 'Forms',
                  icon: 'interactive',
                  value: 'forms',
                },
                {
                  label: 'Documents',
                  icon: 'file-outline',
                  value: 'documents',
                },
                {
                  label: 'Dashboards',
                  icon: 'analytics',
                  value: 'dashboards',
                },
                ...customPins,
              ],
              value: activeTab,
              onChange: (options) => changeActiveTab(options.value),
            }}
          >
            {activeTab === 'forms' && (
              <Grid container>
                <Grid item>
                  <Typography variant="h6">Forms</Typography>
                  <DataGrid
                    id="forms"
                    dataType="forms"
                    viewProps={{ hideModeSwitcher: true }}
                    styles={{ elevation: 0 }}
                  />
                </Grid>
                <Grid item>
                  <Typography variant="h6">Your Requests</Typography>
                  <DataGrid
                    id="jobs"
                    dataType="jobs"
                    viewProps={{ hideModeSwitcher: true }}
                    styles={{ elevation: 0 }}
                  />
                </Grid>
              </Grid>
            )}
            {activeTab === 'documents' && (
              <Grid container>
                <Grid item>
                  <Typography variant="h6">Documents</Typography>
                  <DataGrid
                    id="documents"
                    dataType="documents"
                    viewProps={{ hideModeSwitcher: true }}
                    styles={{ elevation: 0 }}
                  />
                </Grid>
              </Grid>
            )}
            {activeTab === 'dashboards' && (
              <Grid container>
                <Grid item>
                  <Typography variant="h6">Analytics</Typography>
                  <DataGrid
                    id="dashboards"
                    dataType="analytics"
                    viewProps={{ hideModeSwitcher: true }}
                    styles={{ elevation: 0 }}
                  />
                </Grid>
              </Grid>
            )}
            {customActiveTab && <Integration {...customActiveTab.data} />}
            {error && (
              <Snackbar
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                message={'Could not load custom app files: ' + error}
                action={<Icon type="close" onClick={handleSnackbarClose} />}
              />
            )}
          </Page.Common>
        </div>
      </>
    </DocumentTitle>
  );
};

export default Home;
